import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import history from "../store/history";

// Remembers where the user was in a list (page, filters, sorting, scroll...) so that coming
// BACK from a detail page puts them exactly there.
//
// When a list is restored:
//  - the user comes back with the browser's Back button / the back arrow of the detail page
//    (a history POP, which is also what reloading the page is), or
//  - the user left the list through one of its rows (markListLeaving) and gets back to it any
//    other way, e.g. with the sidebar.
// Any other way of arriving at the list (typically the sidebar from another page) starts clean.
//
// State is kept in sessionStorage: per tab, gone when the tab is closed.

const PREFIX = "bo.listState.v1:";
const memory = {}; // fallback for when sessionStorage is not available

// Dates in column filters are moment objects: tag them so they survive JSON.
function replacer(key, value) {
  const original = this[key];
  return moment.isMoment(original)
    ? { __moment: original.toISOString() }
    : value;
}

function reviver(key, value) {
  return value &&
    typeof value === "object" &&
    typeof value.__moment === "string"
    ? moment(value.__moment)
    : value;
}

const read = (key) => {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw, reviver) : memory[key] || null;
  } catch (e) {
    return memory[key] || null;
  }
};

const write = (key, record) => {
  memory[key] = record;
  try {
    window.sessionStorage.setItem(
      PREFIX + key,
      JSON.stringify(record, replacer),
    );
  } catch (e) {
    // storage full or blocked: the in-memory copy still serves this page session
  }
};

export const clearListState = (key) => {
  delete memory[key];
  try {
    window.sessionStorage.removeItem(PREFIX + key);
  } catch (e) {
    // nothing to clear
  }
};

/** Forgets every list: for when the user logs out, so the next one does not inherit anything. */
export const clearAllListState = () => {
  Object.keys(memory).forEach((key) => delete memory[key]);
  try {
    const storage = window.sessionStorage;
    Object.keys(storage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => storage.removeItem(key));
  } catch (e) {
    // nothing to clear
  }
};

/** Stores the part of a list's state worth coming back to. Must be JSON friendly. */
export const saveListState = (key, state) => {
  write(key, { ...read(key), state });
};

/** Call when the user leaves the list through a row: it will be restored when they return. */
export const markListLeaving = (key) => {
  write(key, { ...read(key), leaving: true, scrollY: window.scrollY });
};

/**
 * What to restore when the list opens, or null when it has to start clean. Free of side
 * effects, so it is safe to call while rendering: pair it with settleListState.
 * @param {string} key
 * @param {string} [action] how the user got here (PUSH, POP, REPLACE), defaults to the router's
 * @returns {{state: any, scrollY: number} | null}
 */
export const peekListState = (key, action = history.action) => {
  const saved = read(key);
  if (!saved || !(action === "POP" || saved.leaving)) return null;
  return { state: saved.state || {}, scrollY: saved.scrollY || 0 };
};

/** Bookkeeping once the list has opened: drops stale state, or spends the "leaving" mark. */
export const settleListState = (key, restored) => {
  if (!restored) {
    clearListState(key);
    return;
  }
  write(key, { ...read(key), leaving: false });
};

/**
 * Follows the vertical scroll while the list is open and keeps the last value for the next
 * visit. Returns the function that stops following.
 */
export const trackListScroll = (key) => {
  let lastY = window.scrollY;
  const onScroll = () => {
    lastY = window.scrollY;
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => {
    window.removeEventListener("scroll", onScroll);
    const saved = read(key);
    // Leaving through a row already recorded the position at the moment of the click
    write(key, {
      ...saved,
      scrollY: saved && saved.leaving ? saved.scrollY : lastY,
    });
  };
};

const USER_INPUT_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

/**
 * Scrolls to y as soon as the page is tall enough for it (the list renders after its data
 * arrives). Gives up quietly after a while, and as soon as the user scrolls by themselves.
 * @returns {() => void} cancel
 */
export const restoreScrollY = (y, { timeout = 3000, interval = 50 } = {}) => {
  if (!y || y < 1) return () => {};
  let waited = 0;
  let timer = null;
  let done = false;

  const finish = () => {
    done = true;
    clearTimeout(timer);
    USER_INPUT_EVENTS.forEach((e) => window.removeEventListener(e, finish));
  };
  USER_INPUT_EVENTS.forEach((e) =>
    window.addEventListener(e, finish, { passive: true }),
  );

  const step = () => {
    if (done) return;
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (maxY >= y - 1) {
      window.scrollTo(0, y);
      finish();
    } else if (waited >= timeout) {
      window.scrollTo(0, Math.max(0, maxY));
      finish();
    } else {
      waited += interval;
      timer = setTimeout(step, interval);
    }
  };
  step();
  return finish;
};

/** The part of antd's sorter that can be stored and handed back to the Table. */
export const pickSorter = (sorter) =>
  sorter && sorter.order
    ? { columnKey: sorter.columnKey, field: sorter.field, order: sorter.order }
    : undefined;

/**
 * State of a list written with hooks, kept across visits. Same idea as useState, plus:
 *  - restored: true when the state (and the scroll position) of a previous visit was brought back
 *  - restoreScrollOnce(): call it when the data has rendered, to put the scroll back
 *  - markLeaving(): call it when the user leaves through a row
 * @param {string} key unique name of the list. Without a key nothing is remembered.
 * @param {object} defaults state of a clean start. Only JSON friendly values.
 */
export function useListState(key, defaults = {}) {
  const restoredRef = useRef(undefined);
  if (restoredRef.current === undefined) {
    restoredRef.current = key ? peekListState(key) : null;
  }
  const restored = restoredRef.current;

  const [state, setState] = useState(() => ({
    ...defaults,
    ...(restored ? restored.state : {}),
  }));
  const update = useCallback(
    (patch) =>
      setState((prev) => ({
        ...prev,
        ...(typeof patch === "function" ? patch(prev) : patch),
      })),
    [],
  );

  // Declared in this order on purpose: settle first, then persist the state
  useEffect(() => {
    if (key) settleListState(key, restored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (key) saveListState(key, state);
  }, [key, state]);
  useEffect(() => (key ? trackListScroll(key) : undefined), [key]);

  const pendingScrollY = useRef(restored ? restored.scrollY : 0);
  const restoreScrollOnce = useCallback(() => {
    if (pendingScrollY.current) {
      restoreScrollY(pendingScrollY.current);
      pendingScrollY.current = 0;
    }
  }, []);
  const markLeaving = useCallback(() => {
    if (key) markListLeaving(key);
  }, [key]);

  return {
    state,
    update,
    restored: !!restored,
    restoreScrollOnce,
    markLeaving,
  };
}
